import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box, Paper, Button, Alert, CircularProgress, Stack, TextField, MenuItem,
  FormControlLabel, Switch, Chip, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Table, TableHead, TableBody, TableRow, TableCell, useMediaQuery, useTheme,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { NotificationsActive as ReminderIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsSuperAdmin } from '../../../contexts/PermissionsContext';
import { FEATURES, useFeature } from '../../../contexts/FeatureContext';

export const AUDIENCES = [
  { value: 'Players', label: 'Full-time players', noun: 'player' },
  { value: 'Spares', label: 'Spares', noun: 'spare' },
];

export const REMINDER_TEMPLATES = {
  Players: [
    { value: 'FiveDayAll', label: '5-day reminder', description: 'All rostered players on teams playing that date, with their current availability.' },
    { value: 'FourDayNoResponse', label: '4-day reminder', description: "Rostered players who haven't RSVPed yet." },
    { value: 'ThreeDayNoResponse', label: '3-day reminder', description: "Rostered players who haven't RSVPed yet." },
    { value: 'TwoDayNoResponse', label: '2-day reminder', description: "Rostered players who haven't RSVPed yet." },
  ],
  Spares: [
    { value: 'SpareAvailability', label: '5-day availability check', description: 'Active spare pool members not rostered on a team playing that date.' },
    { value: 'FourDayNoResponse', label: '4-day follow-up', description: "Active spares who haven't responded with their availability for that date." },
    { value: 'ThreeDayNoResponse', label: '3-day follow-up', description: "Active spares who haven't responded with their availability for that date." },
  ],
};

// Older history rows may predate the audience field; spare availability checks always went to spares.
const resolveAudience = (reminder) => reminder.audience
  || (reminder.reminderType === 'SpareAvailability' ? 'Spares' : 'Players');

const audienceLabel = (audience) => AUDIENCES.find((a) => a.value === audience)?.label || audience;

const templateLabel = (audience, value) =>
  (REMINDER_TEMPLATES[audience] || []).find((t) => t.value === value)?.label || value;

const STATUS_COLORS = {
  Pending: 'info',
  Processing: 'warning',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'default',
};

// Dates/times from the API are tenant-local wall-clock values; format from their
// components directly so the browser timezone never shifts them.
export const formatGameDate = (value) => {
  if (!value) return '';
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(new Date(y, m - 1, d));
};

export const formatLocalTime = (value) => {
  if (!value || value.length < 16) return '';
  const hours = Number(value.slice(11, 13));
  const minutes = value.slice(14, 16);
  const suffix = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${displayHours}:${minutes} ${suffix}`;
};

const LeagueName = ({ reminder }) => (reminder.leagueSlug ? (
  <RouterLink to={`/leagues/${reminder.leagueSlug}`}>{reminder.leagueName}</RouterLink>
) : reminder.leagueName);

const formatLocalDateTime = (value) => (value ? `${formatGameDate(value)}, ${formatLocalTime(value)}` : '');

const ManualGameReminderPage = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState('');
  const [dates, setDates] = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [gameDate, setGameDate] = useState('');
  const [audience, setAudience] = useState('Players');
  const sparesEnabled = useFeature(FEATURES.SPARES, leagueId || undefined);
  const [reminderType, setReminderType] = useState('FiveDayAll');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [scheduleSend, setScheduleSend] = useState(false);
  const [scheduledSendDateTime, setScheduledSendDateTime] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await unifiedApi.getManualGameReminders();
      setHistory(data || []);
    } catch (err) {
      console.error('Failed to load manual reminder history', err);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const data = await unifiedApi.getLeagues();
        setLeagues(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load leagues.');
      }
    })();
    loadHistory();
  }, [isSuperAdmin, loadHistory]);

  useEffect(() => {
    setDates([]);
    setGameDate('');
    setPreview(null);
    if (!leagueId) return;
    let cancelled = false;
    setDatesLoading(true);
    (async () => {
      try {
        const data = await unifiedApi.getManualReminderGameDates(leagueId);
        if (!cancelled) setDates(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load game dates.');
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  useEffect(() => {
    setPreview(null);
    if (!leagueId || !gameDate || !reminderType) return;
    let cancelled = false;
    setPreviewLoading(true);
    (async () => {
      try {
        const data = await unifiedApi.previewManualGameReminder(leagueId, gameDate, reminderType, audience);
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load preview.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId, gameDate, reminderType, audience]);

  // Spares can't be reminded in a league (or organization) that has spares switched off.
  useEffect(() => {
    if (!sparesEnabled && audience === 'Spares') {
      setAudience('Players');
      setReminderType(REMINDER_TEMPLATES.Players[0].value);
    }
  }, [sparesEnabled, audience]);

  const handleAudienceChange = (_event, value) => {
    if (!value || value === audience) return;
    setAudience(value);
    setReminderType(REMINDER_TEMPLATES[value][0].value);
  };

  const canSend = !!preview && preview.recipientCount > 0 && !sending
    && (!scheduleSend || !!scheduledSendDateTime);

  const handleSend = async () => {
    setConfirmOpen(false);
    setSending(true);
    setError(null);
    setResult(null);
    try {
      const data = await unifiedApi.sendManualGameReminder({
        leagueId: Number(leagueId),
        gameDate,
        reminderType,
        audience,
        scheduledSendDateTime: scheduleSend ? scheduledSendDateTime : null,
      });
      if (data?.status === 'Failed') {
        setError(data.lastError || 'The reminder failed to send.');
      } else {
        setResult(data);
      }
      loadHistory();
    } catch (err) {
      setError(err.message || 'Failed to send reminder.');
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    setError(null);
    try {
      await unifiedApi.cancelManualGameReminder(id);
      loadHistory();
    } catch (err) {
      setError(err.message || 'Failed to cancel scheduled reminder.');
    }
  };

  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">You must be a Super Admin to access this page.</Alert>
        </Box>
      </AdminLayout>
    );
  }

  const audienceTemplates = REMINDER_TEMPLATES[audience];
  const selectedTemplate = audienceTemplates.find((t) => t.value === reminderType);
  const recipientNoun = AUDIENCES.find((a) => a.value === audience)?.noun || 'recipient';
  const selectedLeague = leagues.find((l) => String(l.id) === String(leagueId));

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <ReminderIcon color="primary" />
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Send Game Reminder
          </Typography>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Send a one-time game reminder to full-time players or spares for every game in a league on a date,
          even if they already received it. Recipients who haven&apos;t received this reminder type are recorded
          as having received it, so the automated process won&apos;t resend it.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
            {result.status === 'Pending'
              ? `Reminder scheduled for ${formatLocalDateTime(result.scheduledSendLocal)} (organization timezone).`
              : `Reminder sent: ${result.recipientCount} email${result.recipientCount === 1 ? '' : 's'} queued.`}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Send to</Typography>
          <ToggleButtonGroup
            value={audience}
            exclusive
            onChange={handleAudienceChange}
            color="primary"
            size="small"
            fullWidth={isMobile}
            aria-label="Audience"
            sx={{ mb: 2 }}
          >
            {AUDIENCES.map((a) => (
              <ToggleButton key={a.value} value={a.value} disabled={a.value === 'Spares' && !sparesEnabled}>
                {a.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {!sparesEnabled && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: -1, mb: 2 }}>
              Spares are turned off {leagueId ? 'for this league' : 'for your organization'}.
            </Typography>
          )}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              select
              label="League"
              value={leagueId}
              onChange={(e) => setLeagueId(e.target.value)}
              fullWidth
            >
              {leagues.map((league) => (
                <MenuItem key={league.id} value={String(league.id)}>{league.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Game date"
              value={gameDate}
              onChange={(e) => setGameDate(e.target.value)}
              fullWidth
              disabled={!leagueId || datesLoading}
              helperText={leagueId && !datesLoading && dates.length === 0 ? 'No upcoming games in this league.' : ' '}
            >
              {dates.map((d) => (
                <MenuItem key={d.date} value={d.date}>
                  {formatGameDate(d.date)} · {d.gameCount} game{d.gameCount === 1 ? '' : 's'} · first at {formatLocalTime(d.firstGameStartLocal)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Template"
              value={reminderType}
              onChange={(e) => setReminderType(e.target.value)}
              fullWidth
              helperText={selectedTemplate?.description || ' '}
            >
              {audienceTemplates.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </Paper>

        {previewLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
        )}

        {preview && !previewLoading && (
          <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Preview</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
              <Chip color="primary" clickable={false} onClick={() => {}} label={`Estimated recipients: ${preview.recipientCount}`} />
              <Chip variant="outlined" clickable={false} onClick={() => {}} label={`Already received this reminder: ${preview.alreadyReceivedCount}`} />
            </Stack>

            <Typography variant="subtitle2" gutterBottom>Games</Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {preview.games.map((g) => (
                <Typography key={g.gameId} variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {formatLocalTime(g.startLocal)} — {g.homeTeamName} vs {g.awayTeamName || 'BYE'} · {g.venueName}
                </Typography>
              ))}
            </Stack>

            {preview.recipientCount === 0 ? (
              <Alert severity="warning">No {recipientNoun}s match this template for the selected date.</Alert>
            ) : (
              <>
                <Typography variant="subtitle2">Subject</Typography>
                <Typography variant="body2" sx={{ mb: 2, wordBreak: 'break-word' }}>{preview.subject}</Typography>
                <Typography variant="subtitle2" gutterBottom>Email (sample recipient)</Typography>
                <Box
                  component="iframe"
                  title="Reminder email preview"
                  sandbox=""
                  srcDoc={preview.htmlBody}
                  sx={{ width: '100%', height: { xs: 480, sm: 560 }, border: 1, borderColor: 'divider', borderRadius: 1 }}
                />
              </>
            )}
          </Paper>
        )}

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Send</Typography>
          <FormControlLabel
            control={<Switch checked={scheduleSend} onChange={(e) => setScheduleSend(e.target.checked)} />}
            label="Schedule for later"
          />
          {scheduleSend && (
            <TextField
              label="Send date and time"
              type="datetime-local"
              value={scheduledSendDateTime}
              onChange={(e) => setScheduledSendDateTime(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
              helperText="Interpreted in your organization's timezone. Scheduled reminders are sent within 15 minutes of this time."
            />
          )}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
              fullWidth={isMobile}
              startIcon={sending ? <CircularProgress size={20} /> : <ReminderIcon />}
            >
              {scheduleSend ? 'Schedule Reminder' : 'Send Reminder'}
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom>Recent manual reminders</Typography>
          {history.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No manual reminders yet.</Typography>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {history.map((h) => (
                <Paper key={h.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}><LeagueName reminder={h} /></Typography>
                    <Chip size="small" clickable={false} onClick={() => {}} label={h.status} color={STATUS_COLORS[h.status] || 'default'} />
                  </Stack>
                  <Typography variant="body2">
                    {audienceLabel(resolveAudience(h))} · {templateLabel(resolveAudience(h), h.reminderType)} · games on {formatGameDate(h.gameDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Send time: {formatLocalDateTime(h.scheduledSendLocal)}</Typography>
                  <Typography variant="body2" color="text.secondary">Emails queued: {h.recipientCount}</Typography>
                  {h.lastError && <Typography variant="body2" color="error">{h.lastError}</Typography>}
                  {h.status === 'Pending' && (
                    <Button size="small" color="error" fullWidth sx={{ mt: 1 }} onClick={() => handleCancelScheduled(h.id)}>
                      Cancel
                    </Button>
                  )}
                </Paper>
              ))}
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>League</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Game date</TableCell>
                  <TableCell>Send time</TableCell>
                  <TableCell>Emails</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell><LeagueName reminder={h} /></TableCell>
                    <TableCell>{audienceLabel(resolveAudience(h))}</TableCell>
                    <TableCell>{templateLabel(resolveAudience(h), h.reminderType)}</TableCell>
                    <TableCell>{formatGameDate(h.gameDate)}</TableCell>
                    <TableCell>{formatLocalDateTime(h.scheduledSendLocal)}</TableCell>
                    <TableCell>{h.recipientCount}</TableCell>
                    <TableCell>
                      <Chip size="small" clickable={false} onClick={() => {}} label={h.status} color={STATUS_COLORS[h.status] || 'default'} title={h.lastError || ''} />
                    </TableCell>
                    <TableCell align="right">
                      {h.status === 'Pending' && (
                        <Button size="small" color="error" onClick={() => handleCancelScheduled(h.id)}>Cancel</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>

        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>{scheduleSend ? 'Schedule game reminder?' : 'Send game reminder?'}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {scheduleSend ? 'Schedule' : 'Send'} the <strong>{selectedTemplate?.label}</strong> for{' '}
              <strong>{selectedLeague?.name}</strong> games on <strong>{formatGameDate(gameDate)}</strong> to
              approximately <strong>{preview?.recipientCount ?? 0}</strong> {recipientNoun}{preview?.recipientCount === 1 ? '' : 's'}
              {scheduleSend && scheduledSendDateTime
                ? ` at ${formatLocalDateTime(scheduledSendDateTime)} (organization timezone)`
                : ''}.
              {preview?.alreadyReceivedCount > 0 && ` ${preview.alreadyReceivedCount} of them already received this reminder and will get it again.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSend}>
              {scheduleSend ? 'Schedule' : 'Send Now'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default ManualGameReminderPage;
